import React, { useEffect, useMemo } from "react";
import { withStyles } from "@material-ui/core/styles";
import createStyles from "@material-ui/core/styles/createStyles";
import { Theme } from "@material-ui/core/styles/createMuiTheme";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import DashboardIcon from "@material-ui/icons/Dashboard";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import VerifiedUserIcon from "@material-ui/icons/VerifiedUser";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import { RouteComponentProps, withRouter } from "react-router-dom";
import GlobalTab from "./tabs/GlobalTab";
import ProductiviteTab from "./tabs/ProductiviteTab";
import QualiteTab from "./tabs/QualiteTab";
import IncidentsTab from "./tabs/incident-tab";
import { parseAnalyseQueryParams } from "./utils";
import { appPageRootFullHeight } from "../appBar/appBarTheme";
import { analyseContentCardViewportOffset, analyseTheme } from "./analyseTheme";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      ...appPageRootFullHeight(theme),
    },
    tabsContainer: {
      width: "100%",
      marginBottom: theme.spacing.unit * 2,
      backgroundColor: analyseTheme.surface,
      borderRadius: analyseTheme.radiusSm,
      boxShadow: "none",
      border: `1px solid ${analyseTheme.border}`,
      overflow: "hidden",
    },
    tabsRoot: {
      backgroundColor: analyseTheme.surface,
      minHeight: 44,
    },
    tabsFlexContainer: {
      gap: "6px",
      padding: "4px 8px",
    },
    tabsIndicator: {
      display: "none",
    },
    tabRoot: {
      minHeight: 36,
      minWidth: "auto",
      maxWidth: "none",
      padding: 0,
      textTransform: "none",
      fontSize: "0.8125rem",
      fontWeight: 500,
      color: analyseTheme.textPrimary,
      opacity: 1,
      overflow: "visible",
      "&:hover": {
        backgroundColor: "transparent",
        opacity: 1,
      },
      "&$tabSelected": {
        color: analyseTheme.secondaryDark,
      },
      "&$tabSelected $tabWrapper": {
        backgroundColor: analyseTheme.secondarySoft,
        border: `1px solid rgba(255, 94, 98, 0.25)`,
        color: analyseTheme.secondaryDark,
      },
      "&$tabSelected $tabIcon": {
        color: theme.palette.secondary.main,
      },
    },
    tabLabelIcon: {
      minHeight: 36,
      paddingTop: 0,
      paddingBottom: 0,
    },
    tabWrapper: {
      flexDirection: "row",
      alignItems: "center",
      gap: "6px",
      width: "auto",
      padding: "4px 10px",
      borderRadius: analyseTheme.radiusSm,
      border: "1px solid transparent",
      transition: "background-color 0.15s ease, border-color 0.15s ease",
    },
    tabLabelContainer: {
      padding: 0,
      width: "auto",
    },
    tabSelected: {},
    tabIcon: {
      color: analyseTheme.textSecondary,
      fontSize: 18,
      marginBottom: 0,
    },
    tabLabel: {
      color: "inherit",
      lineHeight: 1.2,
      fontWeight: 500,
    },
    contentCard: {
      width: "100%",
      backgroundColor: analyseTheme.surface,
      border: `1px solid ${analyseTheme.border}`,
      borderRadius: analyseTheme.radius,
      boxShadow: "none",
      padding: theme.spacing.unit * 3,
      minHeight: `calc(100vh - ${analyseContentCardViewportOffset(theme)}px)`,
    },
  });

interface RouteParams {
  tabName?: string;
}

interface PropsType extends RouteComponentProps<RouteParams> {
  classes: any;
}

const analyseTabs = [
  // {
  //   id: "vue-globale",
  //   label: "Vue globale",
  //   Icon: DashboardIcon,
  //   component: GlobalTab,
  // },
  // {
  //   id: "productivite",
  //   label: "Productivité",
  //   Icon: TrendingUpIcon,
  //   component: ProductiviteTab,
  // },
  // {
  //   id: "qualite",
  //   label: "Qualité",
  //   Icon: VerifiedUserIcon,
  //   component: QualiteTab,
  // },
  {
    id: "incidents",
    label: "Incidents",
    Icon: ReportProblemIcon,
    component: IncidentsTab,
  },
];

const defaultTabId = analyseTabs[0].id;

const Analyse = ({ classes, match, history, location }: PropsType) => {
  const requestedTab = match.params.tabName;
  const activeTabIndex = analyseTabs.findIndex((tab) => tab.id === requestedTab);
  const normalizedTabIndex = activeTabIndex >= 0 ? activeTabIndex : 0;
  const currentTab = analyseTabs[normalizedTabIndex];
  const CurrentTabComponent = currentTab.component;
  const needsRedirect = !requestedTab || activeTabIndex === -1;

  const params = useMemo(() => parseAnalyseQueryParams(location.search), [location.search]);

  useEffect(() => {
    if (!needsRedirect) return;
    history.replace(`/analyse/${defaultTabId}${location.search}`);
  }, [needsRedirect, history, location.search]);

  if (needsRedirect) {
    return null;
  }

  const handleTabChange = (_event: any, value: number) => {
    const nextTabId = analyseTabs[value].id;
    history.push(`/analyse/${nextTabId}${location.search}`);
  };

  return (
    <Grid container direction="column" wrap="nowrap" className={classes.root}>
      <Grid item style={{ width: "100%" }}>
        <Paper className={classes.tabsContainer} elevation={0} square={false}>
          <Tabs
            value={normalizedTabIndex}
            onChange={handleTabChange}
            indicatorColor="secondary"
            textColor="inherit"            
            scrollButtons="auto"
            classes={{
              root: classes.tabsRoot,
              flexContainer: classes.tabsFlexContainer,
              indicator: classes.tabsIndicator,
            }}
          >
            {analyseTabs.map((tab) => (
              <Tab
                key={tab.id}
                classes={{
                  root: classes.tabRoot,
                  selected: classes.tabSelected,
                  labelIcon: classes.tabLabelIcon,
                  wrapper: classes.tabWrapper,
                  labelContainer: classes.tabLabelContainer,
                  label: classes.tabLabel,
                }}
                icon={<tab.Icon className={classes.tabIcon} fontSize="small" />}
                label={tab.label}
              />
            ))}
          </Tabs>
        </Paper>
      </Grid>

      <Grid item style={{ width: "100%" }}>
        <Paper className={classes.contentCard} elevation={0} square={false}>
          <CurrentTabComponent
            params={params}
            tabLabel={currentTab.label}
            TabIcon={currentTab.Icon}
          />
        </Paper>
      </Grid>
    </Grid>
  );
};

export default withStyles(styles)(withRouter(Analyse));
